import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrefetch } from '@/shared/lib/hooks/api/usePrefetch';
import type { ISidebarRoute } from '../types/sidebar.types';

/**
 * Custom hook for handling sidebar navigation logic
 * @returns Navigation functions
 */
export const useSidebarNavigation = () => {
  const navigate = useNavigate();
  const { prefetchLinks } = usePrefetch();

  /**
   * Handle menu item click
   * @param item - Menu item to navigate to
   */
  const handleMenuItemClick = useCallback((item: ISidebarRoute) => {
    if (!item.path) return;
    
    // Navigate to the route
    navigate(item.path);
    
    // Prefetch related links if available
    if (item.prefetchLinks?.length) {
      prefetchLinks(item.prefetchLinks);
    }
  }, [navigate, prefetchLinks]);

  /**
   * Handle mouse enter for prefetching
   * @param item - Menu item that was hovered
   */
  const handleMenuItemHover = useCallback((item: ISidebarRoute) => {
    // Prefetch links on hover for better performance
    if (item.prefetchLinks?.length) {
      prefetchLinks(item.prefetchLinks);
    }
  }, [prefetchLinks]);

  /**
   * Handle submenu item click
   * @param item - Submenu item to navigate to
   * @param event - Click event
   */
  const handleSubmenuItemClick = useCallback((item: ISidebarRoute, event: React.MouseEvent) => {
    // Prevent event bubbling to parent
    event.stopPropagation();
    
    if (!item.path) {
      console.log('No path found for item:', item);
      return;
    }
    
    navigate(item.path);
    
    // Prefetch related links if available
    if (item.prefetchLinks?.length) {
      prefetchLinks(item.prefetchLinks);
    }
  }, [navigate, prefetchLinks]);

  return {
    handleMenuItemClick,
    handleMenuItemHover,
    handleSubmenuItemClick,
  };
};
